import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { Delivery } from "@types_def/models/delivery.types";
import { UserRank } from "@types_def/models/user.type";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useDataTableSelectionBarContext } from "@/components/ui/data-table-selection-bar";
import {
  type BulkDeliveryReportResult,
  downloadOrGenerateDeliveryReportsSequentially,
} from "@/utils/delivery-report";

import { BulkSelectionConfirmDialog } from "./bulk-selection-confirm-dialog";
import { useBulkSelectionActionVisibility } from "./use-bulk-selection-action-visibility";

function formatDeliveryIds(ids: number[]) {
  return ids.join(", ");
}

function buildBulkDownloadResultSummary(
  t: (key: string, options?: Record<string, unknown>) => string,
  result: BulkDeliveryReportResult
) {
  const sections: string[] = [];

  if (result.downloaded.length > 0) {
    sections.push(
      t("delivery.pdf-report.bulk.result.downloaded", {
        count: result.downloaded.length,
        ids: formatDeliveryIds(result.downloaded),
      })
    );
  }
  if (result.generating.length > 0) {
    sections.push(
      t("delivery.pdf-report.bulk.result.generating", {
        count: result.generating.length,
        ids: formatDeliveryIds(result.generating),
      })
    );
  }
  if (result.notReady.length > 0) {
    sections.push(
      t("delivery.pdf-report.bulk.result.notReady", {
        count: result.notReady.length,
        ids: formatDeliveryIds(result.notReady),
      })
    );
  }
  if (result.failed.length > 0) {
    sections.push(
      t("delivery.pdf-report.bulk.result.failed", {
        count: result.failed.length,
        ids: formatDeliveryIds(result.failed),
      })
    );
  }

  return sections.join("\n\n");
}

function getBulkDownloadResultTitle(t: (key: string) => string, result: BulkDeliveryReportResult) {
  const { downloaded, generating, notReady, failed } = result;
  const total = downloaded.length + generating.length + notReady.length + failed.length;

  if (downloaded.length === total) {
    return t("delivery.pdf-report.bulk.result.allDownloaded.title");
  }
  if (generating.length === total) {
    return t("delivery.pdf-report.bulk.result.allGenerating.title");
  }
  if (notReady.length === total) {
    return t("delivery.pdf-report.bulk.result.allNotReady.title");
  }
  if (failed.length === total) {
    return t("delivery.pdf-report.bulk.result.allFailed.title");
  }

  return t("delivery.pdf-report.bulk.result.partial.title");
}

function getBulkDownloadResultInstruction(
  t: (key: string) => string,
  result: BulkDeliveryReportResult
) {
  if (result.generating.length > 0) {
    return t("delivery.pdf-report.bulk.result.generatingInstruction");
  }

  if (result.failed.length > 0) {
    return t("delivery.pdf-report.error.instruction");
  }

  return undefined;
}

export function BulkDownloadReportAction() {
  const visible = useBulkSelectionActionVisibility({
    requireAdmin: true,
    minRank: UserRank.SUPPORT,
  });
  const { selectedData, table } = useDataTableSelectionBarContext<Delivery>();
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [result, setResult] = useState<BulkDeliveryReportResult | null>(null);

  const count = selectedData.length;

  const resultSummary = useMemo(() => {
    if (!result) {
      return "";
    }

    return buildBulkDownloadResultSummary(t, result);
  }, [result, t]);

  const resultTitle = useMemo(() => {
    if (!result) {
      return "";
    }

    return getBulkDownloadResultTitle(t, result);
  }, [result, t]);

  const resultInstruction = useMemo(() => {
    if (!result) {
      return undefined;
    }

    return getBulkDownloadResultInstruction(t, result);
  }, [result, t]);

  if (!visible) {
    return null;
  }

  const handleConfirm = async () => {
    setIsPending(true);
    try {
      const outcome = await downloadOrGenerateDeliveryReportsSequentially(selectedData);
      setConfirmOpen(false);
      setResult(outcome);

      if (
        outcome.notReady.length === 0 &&
        outcome.failed.length === 0 &&
        outcome.generating.length === 0
      ) {
        table.resetRowSelection();
      }
    } finally {
      setIsPending(false);
    }
  };

  const closeResultDialog = () => setResult(null);

  return (
    <>
      <Tooltip title={t("delivery.action.download-reports.title")}>
        <span>
          <Button
            color="info"
            disabled={count === 0 || isPending}
            onClick={() => setConfirmOpen(true)}
            size="small"
            startIcon={<CloudDownloadIcon fontSize="small" />}
            variant="contained"
          >
            {isPending
              ? t("common.shared.downloading")
              : t("delivery.action.download-reports.text")}
          </Button>
        </span>
      </Tooltip>

      <BulkSelectionConfirmDialog
        alertSeverity="info"
        confirmColor="info"
        confirmLabel={t("delivery.action.download-reports.text")}
        description={t("delivery.pdf-report.bulk.confirm.description", { count })}
        isPending={isPending}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        open={confirmOpen}
        title={t("delivery.pdf-report.bulk.confirm.title")}
      />

      <Dialog
        aria-describedby="bulk-download-report-result-description"
        aria-labelledby="bulk-download-report-result-title"
        fullWidth
        maxWidth="sm"
        onClose={closeResultDialog}
        open={result !== null}
      >
        <DialogTitle id="bulk-download-report-result-title">{resultTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography
              id="bulk-download-report-result-description"
              sx={{ whiteSpace: "pre-line" }}
              variant="body2"
            >
              {resultSummary}
            </Typography>
            {resultInstruction ? (
              <Typography color="text.secondary" variant="body2">
                {resultInstruction}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ gap: 1, px: 3, pb: 2 }}>
          <Button color="inherit" onClick={closeResultDialog} variant="contained">
            {t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
